import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v1 as uuid } from 'uuid';
// eslint-disable-next-line import/no-extraneous-dependencies
import TextareaAutosize from '@mui/base/TextareaAutosize';
import styles from './docDetailModal.module.css';
import mainStyles from '../../pages/main.module.css';
import {
  Document,
  DocumentClassificationOption,
  DocumentDetail,
  DocumentUpdateForm,
} from '../../services/http/documents.api';
import {
  useDocumentAttachmentDownload,
  useDocumentDelete,
  useDocumentDeleteRevision,
  useDocumentDownload,
  useDocumentRevision,
  useDocumentUpdate,
} from '../../hooks/api/document.hook';
import PdfPreview, { PdfScrollMode } from '../PdfPreview/PdfPreview';
import SelectInput from '../SelectInput';
import Button from '../Button/button';
import { useAlertModal, useMessageModal } from '../../hooks/modal.hook';
import BaseModal from '../BaseModal';
import style from '../AlertModal/alertModal.module.css';
import { ApiError } from '../../services/http';
import { useGetRelatedTagsApi } from '../../hooks/api/tag.hook';
import { useGetDocumentClassificationAndTypeApi } from '../../hooks/api/documentManagement.hook';
import { DocumentClassificationAndType } from '../../services/http/documentManagement.api';
import { DropDownMenuItem } from '../DropdownMenu';
import { useDownloadFile } from '../../hooks/file.hook';
import FileInputButton from '../ButtonFileInput';
import RadioGroupInput from '../RadioGroupInput';
import TagInput from '../TagInput';
// eslint-disable-next-line import/no-cycle
import ChooseRelatedDocument from '../../pages/docRegisterScreen/chooseRelatedDocument';
import { useAuthContext } from '../../store/auth.store';
import ControlCode from '../../utils/controlCode';
import { useCreateDownloadFileLogApi, useCreateLogApi } from '../../hooks/api/log.hook';
import { PdfPreviewModal } from '../PdfPreviewModal';
import InputAutoSize from '../InputAutoSize/inputAutoSize';
import Validator from '../../utils/validators';
import { ERROR_MESSAGES } from '../../utils/messages';
import useCheckDuplicatedTags from '../../hooks/useCheckDuplicatedTags.hook';
import AlertModal, { alertModalInitialState, AlertModalProps } from '../AlertModal';

/**
 * 関連文書のインターフェース
 */
export interface RelationalDocument {
  id?: number;
  name?: string;
  type?: string;
  duplicateTags?: { label: string; value: string; }[];
}

/**
 * 文書詳細モーダル’編集’のプロップスインターフェース
 */
export interface Props {
  /** 使用される文書の参照 */
  document: DocumentDetail | null;
  /** バージョンが削除されるきに呼び出される関数 */
  onRemoveDocumentVersion: (version: DocumentDetail['documents'][number]) => void,
  /** 文書が削除されるときに呼び出される関数 */
  onRemoveDocument: (doc: DocumentDetail) => void,
  /** バージョンが更新されるときに呼び出される関数 */
  onUpdateVersion: (form: DocumentUpdateForm) => void,
  /** モーダルが閉じられるときに呼び出される関数 */
  onClose: (hidden:boolean) => void
  /** 文書が更新されるときに呼び出される関数 */
  onDocumentUpdated: () => void
  /** 編集中のバージョン番号 */
  editingVersionNo: number;
  /** 関連文書の追加がキャンセルされたときに呼び出される関数 */
  onCancelAddingRelatedDocuments: () => void;
  /** 関連文書の追加がキャンセルされたときに呼び出される関数 */
  highlightTexts?: string[];
  /** ハイライトするテキストの配列 */
  searchKeyword?: string;
  flatTags?: { tagLabel: string; value: string }[];
}

/**
 * 文書詳細モーダル’編集’のコンポーネント
 */
function DocDetailEdit({
  document: documentRef, onRemoveDocumentVersion,
  onUpdateVersion, onRemoveDocument, onClose,
  onDocumentUpdated, editingVersionNo, onCancelAddingRelatedDocuments,
  highlightTexts = [], searchKeyword, flatTags = [],

} :Props) {
  const inputAttachmentFile = useRef<HTMLInputElement | null>(null);
  const fileSelectorRef = useRef<HTMLInputElement>(null);
  const fileAttachmentRefs = useRef<HTMLInputElement[]>([]);

  const [operationModal, setOperationModal] = useState(false);
  const [documentVersion, setDocumentVersion] = useState<DocumentDetail['documents'][number] | null>(null);
  const [documentClassifications, setDocumentClassifications] = useState<DocumentClassificationAndType[]>([]);
  const [documentPrintedTypeSelected, setDocumentPrintedTypeSelected] = useState<'ELECTRONIC' | 'PAPER'>('ELECTRONIC');
  const [choosingRelatedDocument, setChoosingRelatedDocument] = useState(false);
  const [editingVersionState, setEditingVersionState] = useState<any>();
  const [isDeletionDisabled, setIsDeletionDisabled] = useState(false);
  const [pdfForModal, setPdfForModal] = useState<File | null>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [modal, setModal] = useState<AlertModalProps>(alertModalInitialState);

  const { hasFolderPermission } = useAuthContext();

  const [form, setForm] = useState<DocumentUpdateForm>({
    attachmentFileList: [],
    barcodePrinting: '',
    classification: 0,
    electronicFlg: documentRef?.electronicFlg || 0,
    file: undefined,
    fileName: '',
    fileSize: 0,
    fileId: undefined,
    memo: '',
    paperFlg: documentRef?.paperFlg || 0,
    relationalDocumentList: [],
    tagList: [],
    typeId: 0,
    typeName: '',
    name: '',
    id: 0,
  });

  const documentDownload = useDocumentDownload();
  const documentUpdate = useDocumentUpdate();
  const documentRevision = useDocumentRevision();
  const documentDelete = useDocumentDelete();
  const documentDeleteRevision = useDocumentDeleteRevision();
  const openAlertModal = useAlertModal();
  const openMessageModal = useMessageModal();
  const getDocumentClassificationAndTypeApi = useGetDocumentClassificationAndTypeApi();
  const getDefaultTag = useGetRelatedTagsApi();
  const downloadFile = useDownloadFile();
  const documentAttachmentDownload = useDocumentAttachmentDownload();
  const { request: createLog } = useCreateLogApi();
  const { request: createDownloadLog } = useCreateDownloadFileLogApi();
  const [isFormBodySizeTooLarge, setIsFormBodySizeTooLarge] = useState(false);

  const { checkDuplicatesForSingleForm } = useCheckDuplicatedTags({
    ignoreDocumentId: documentRef?.id,
  });

  const hasDuplicatedTagValue = (formData: DocumentUpdateForm) => formData.tagList.some((tag) => (tag as any).isDuplicated === true);

  const hasRequiredTagNotFilled = (formData: DocumentUpdateForm) => formData.tagList?.some((tag) => tag.required && !tag.value);

  const isButtonRegisterDisabled = (
    isFormBodySizeTooLarge
    || !form.name
    || Validator.isInvalidDocumentName(form.name)
    || form.tagList.some(({ isValidValue }) => isValidValue === false)
    || hasDuplicatedTagValue(form)
    || hasRequiredTagNotFilled(form)
    || Boolean(form.fileError)
    || Boolean(form.fileNameError)
    || form.attachmentFileList.some(({ fileNameError }) => fileNameError)
  );

  const updateVersion = useCallback(async (version: DocumentDetail['documents'][number]) => {
    setIsDeletionDisabled(!(documentRef?.documents[0]?.versionNo === version.versionNo));

    const newForm = {
      ...form,
      id: documentRef?.id ?? 0,
      barcodePrinting: documentRef?.barcodePrinting ?? '',
      name: version.name,
      memo: version.memo,
      fileSize: version.fileSize,
      fileName: version.fileName,
      fileId: version.fileName ? version.fileId : undefined,
      typeId: version.typeId,
      classification: version.classification,
      typeName: version.typeName,
      tagList: version.tags || [],
      relationalDocumentList: version.relationalDocuments,
      attachmentFileList: version.attachmentFileList || [],
    };
    setDocumentVersion(version);
    setEditingVersionState(newForm);
    setForm(newForm);
    setIsFormBodySizeTooLarge(false);
    try {
      if (version.fileName) {
        const pdfFile = await documentDownload.request({ documentDataId: version.dataId, fileName: version.fileName });
        if (pdfFile) {
          newForm.file = pdfFile;
          newForm.fileName = pdfFile.name;
        }
      }
      setDocumentPrintedTypeSelected(documentRef?.electronicFlg === 1 ? 'ELECTRONIC' : 'PAPER');
      getDocumentClassificationAndTypeApi.request().then(setDocumentClassifications);
    } catch (e) {
      await openMessageModal((e as ApiError)?.message);
    }
  }, [documentDownload, documentRef?.barcodePrinting, documentRef?.electronicFlg, documentRef?.id, form, getDocumentClassificationAndTypeApi, openMessageModal]);

  const updateClassificationType = useCallback(async (option: DocumentClassificationOption) => {
    const newForm = {
      ...form,
      classification: option.documentClassification.id || 0,
      typeId: option.type.documentTypeId || 0,
      typeName: option.type.documentTypeName,
    };
    setForm(newForm);
    try {
      const res = await getDefaultTag.request(option.documentClassification.id || 0, option.type.documentTypeId || 0);
      const tagList = res.map((defaultTag) => {
        const documentTag = flatTags.find((tag) => tag.tagLabel === defaultTag.tagLabel);
        return {
          format: defaultTag.format,
          formatCheck: defaultTag.formatCheck,
          value: documentTag?.value || '',
          tagLabel: defaultTag.tagLabel,
          required: defaultTag.require,
        };
      });
      setForm({
        ...newForm,
        tagList,
      });
    } catch (e) {
      await openMessageModal((e as ApiError)?.message);
    }
  }, [form, documentRef, documentVersion, getDefaultTag, openMessageModal]);

  const handleChangeDocumentType = (option: DocumentClassificationOption) => {
    setModal({
      ...modal,
      open: true,
      text: 'タグの編集内容は破棄されます。よろしいですか？',
      onConfirm: () => {
        updateClassificationType(option);
        setModal({ ...modal, open: false });
      },
      onCancel: () => setModal({ ...modal, open: false }),
    });
  };

  const setDocumentVersionTagValue = useCallback((tag: DocumentUpdateForm['tagList'][number], key: keyof DocumentDetail['documents'][number]['tags'][number], value: string, isValidValue?: boolean) => {
    if (!form) return;
    setForm({
      ...form,
      tagList: form.tagList.map((item) => (tag === item ? {
        ...item,
        [key]: value,
        isValidValue,
        isDuplicated: value !== item[key] ? false : item.isDuplicated,
      } : item)),
    });
  }, [form]);

  const setDocumentAttachmentValue = useCallback((
    attachment: DocumentUpdateForm['attachmentFileList'][number],
    key: keyof DocumentUpdateForm['attachmentFileList'][number],
    value: DocumentUpdateForm['attachmentFileList'][number][keyof DocumentUpdateForm['attachmentFileList'][number]],
  ) => {
    setForm({ ...form, attachmentFileList: form.attachmentFileList.map((item) => (attachment === item ? { ...item, [key]: value } : item)) });
  }, [form]);

  const removeAttachmentFile = useCallback(async (attachment: DocumentUpdateForm['attachmentFileList'][number]) => {
    const updatedAttachmentFileList = form.attachmentFileList.map((item) => {
      if (item.id && item.id === attachment.id) {
        return { ...item, deleted: true };
      }
      return item;
    }).filter((item) => item.id || item !== attachment);
    const newForm = { ...form, attachmentFileList: updatedAttachmentFileList };

    const isTooLarge = await Validator.validateDocumentUpdateFormIsTooLarge(newForm);
    setIsFormBodySizeTooLarge(isTooLarge);
    setForm(newForm);
  }, [form]);

  const downloadAttachmentFile = useCallback(async (attachFile: { id?: number, fileName?: string, file?: File }, pdfName?: string) => {
    if (documentVersion) {
      const attachmentFile = attachFile.file ?? await documentAttachmentDownload.request({ attachmentId: attachFile.id as number });
      downloadFile(attachmentFile, pdfName);
    }
  }, [createLog, documentAttachmentDownload, documentVersion, downloadFile]);

  const onDownloadClick = useCallback(() => {
    if (form.file) {
      if (documentVersion) createDownloadLog(documentVersion.dataId, 0);

      downloadFile(form.file, form.fileName);
    }
  }, [createLog, downloadFile, form]);

  const onDeleteRegisterClick = useCallback(async () => {
    if (!documentRef || !documentVersion) return;
    await openAlertModal('文書を削除してよろしいですか？');
    try {
      await documentDelete.request({ id: documentRef.id });
      onRemoveDocument(documentRef);
      onClose(true);
    } catch (e) {
      await openMessageModal((e as ApiError)?.message);
    }
  }, [documentDelete, documentRef, documentVersion, onClose, onRemoveDocument, openAlertModal, openMessageModal]);

  const onDeleteRevisionClick = useCallback(async () => {
    if (!documentRef || !documentVersion) return;
    await openAlertModal('表示中の版情報を削除してよろしいですか？');
    try {
      await documentDeleteRevision.request({ id: documentRef.id, versionId: documentVersion.versionNo });
      onRemoveDocumentVersion(documentVersion);
    } catch (e) {
      await openMessageModal((e as ApiError)?.message);
    }
  }, [documentDeleteRevision, documentRef, documentVersion, onRemoveDocumentVersion, openAlertModal, openMessageModal]);

  const onFileSelected = useCallback(async (file: File | undefined) => {
    const fileNameError = Validator.validateDocumentFileName(file?.name);
    const fileError = await Validator.validateDocumentFile(file as File);

    const newForm = {
      ...form, file, fileName: file?.name, fileSize: file?.size, fileId: undefined, fileError, fileNameError,
    };
    const isTooLarge = await Validator.validateDocumentUpdateFormIsTooLarge(newForm);
    setIsFormBodySizeTooLarge(isTooLarge);
    setForm(newForm);
  }, [form]);

  const documentTypeOptions = useMemo(() => {
    const result: DropDownMenuItem<DocumentClassificationOption>[] = [];
    documentClassifications.forEach((documentClassification) => {
      documentClassification.types.forEach((type) => {
        result.push({
          text: `${documentClassification.name}: ${type.documentTypeName}`,
          value: { documentClassification, type },
        });
      });
    });

    return result;
  }, [documentClassifications]);

  const selectedClassification = useMemo((): DropDownMenuItem<DocumentClassificationOption> | undefined => documentTypeOptions.find((item) => item.value.type.documentTypeId === form.typeId && item.value.documentClassification.id === form.classification), [documentTypeOptions, form.classification, form.typeId]);

  const onDocumentPrintedTypeSelected = useCallback((e: 'ELECTRONIC' | 'PAPER') => {
    if (!selectedClassification?.text.startsWith('契約書')) {
      setForm({ ...form, electronicFlg: e === 'ELECTRONIC' ? 1 : 0, paperFlg: e === 'PAPER' ? 1 : 0 });
    } else if (documentPrintedTypeSelected === 'PAPER' && e === 'ELECTRONIC') {
      setForm({ ...form, electronicFlg: 1, paperFlg: 0 });
    } else {
      setForm({ ...form, electronicFlg: e === 'ELECTRONIC' ? 1 : 0 });
    }
    setDocumentPrintedTypeSelected(e);
  }, [documentPrintedTypeSelected, form, selectedClassification]);

  const onSaveDocument = async (saveType: string) => {
    const updatedForm = {
      ...form,
      attachmentFileList: form.attachmentFileList.filter((item) => !item.deleted),
    };

    if (!selectedClassification?.text.startsWith('契約書')) {
      delete updatedForm.electronicFlg;
      delete updatedForm.paperFlg;
    }
    try {
      if (saveType === 'revision') {
        await documentRevision.request(updatedForm);
      }
      if (saveType === 'update') {
        await documentUpdate.request(updatedForm);
      }
      onUpdateVersion(updatedForm);
      onDocumentUpdated();
      onClose(true);
    } catch (e) {
      if ((e as ApiError).isFileTooLarge) {
        setIsFormBodySizeTooLarge(true);
      }
      await openMessageModal((e as ApiError)?.message);
    }
  };

  const removeSameDocuments = useCallback((documents: Document[]) => documents.filter((document) => !form.relationalDocumentList?.some((item) => Number(item.id) === Number(document.id)) && Number(document.id) !== Number(form.id)), [form]);

  const handleRelatedDocuments = useCallback((documents: Document[]) => {
    const filteredDocuments = removeSameDocuments(documents);

    const documentsToFormFormat = filteredDocuments.map((document) => ({
      ccControlNumber: document.ccControlNumber,
      id: document.id,
      name: document.name,
      type: document.typeId?.toString(),
      duplicateTags: [],
    }));
    const newRelatedDocumentsForm = [...form.relationalDocumentList as [], ...documentsToFormFormat];
    setForm({ ...form, relationalDocumentList: newRelatedDocumentsForm });
    setChoosingRelatedDocument(false);
    onCancelAddingRelatedDocuments();
  }, [form, onCancelAddingRelatedDocuments, removeSameDocuments]);

  const removeRelatedDocument = useCallback((document: RelationalDocument) => {
    setForm({ ...form, relationalDocumentList: form?.relationalDocumentList?.filter((item) => item !== document) });
  }, [form]);

  const onRegister = async () => {
    const updatedForm = {
      ...form,
      attachmentFileList: form.attachmentFileList.filter((item) => !item.deleted),
    };

    const requiredTagNotFilled = updatedForm.tagList?.some((tag) => tag.required && tag.value === '');
    if (requiredTagNotFilled) {
      openMessageModal('全ての必須項目を入力してください。');
      return;
    }

    const isInvalidTag = updatedForm.tagList.some((tag) => tag.isValidValue === false);
    if (isInvalidTag) {
      openMessageModal('タグの値が不正です。');
      return;
    }

    setIsCheckingDuplicates(true);
    const result = await checkDuplicatesForSingleForm({
      tagList: updatedForm.tagList,
    }).finally(() => setIsCheckingDuplicates(false));

    // Update isDuplicated property on each tag based on duplicate check results
    if (result.hasDuplicates && result.originalDuplicatedValues) {
      updatedForm.tagList.forEach((tag) => {
        const isDuplicated = result.originalDuplicatedValues.some((duplicate) => duplicate.tagLabel === tag.tagLabel
          && duplicate.tagValues.includes(tag.value));
        // eslint-disable-next-line no-param-reassign
        (tag as any).isDuplicated = isDuplicated;
      });

      // Update the form state to reflect the duplicate status
      setForm(updatedForm);
      return;
    }
    // Set all tags as not duplicated
    updatedForm.tagList.forEach((tag) => {
      // eslint-disable-next-line no-param-reassign
      (tag as any).isDuplicated = false;
    });

    if (updatedForm.file && (editingVersionState?.fileName !== updatedForm.fileName || editingVersionState?.fileSize !== updatedForm.fileSize)) {
      setOperationModal(true);
    } else {
      try {
        await documentUpdate.request(updatedForm);
        onUpdateVersion(updatedForm);
        onDocumentUpdated();
        onClose(true);
      } catch (e) {
        if ((e as ApiError).isFileTooLarge) {
          setIsFormBodySizeTooLarge(true);
        }
        await openMessageModal((e as ApiError)?.message);
      }
    }
  };

  useEffect(() => {
    const validate = async () => {
      if (form.file) {
        setForm({
          ...form,
          fileError: await Validator.validateDocumentFile(form.file),
        });
      }
    };
    validate();
  }, [form.file]);

  useEffect(() => {
    if (documentRef) {
      setForm({
        id: documentRef.id,
        attachmentFileList: [],
        classification: 0,
        file: undefined,
        fileName: '',
        fileSize: 0,
        fileId: undefined,
        memo: '',
        name: '',
        relationalDocumentList: [],
        tagList: [],
        typeId: 0,
        typeName: '',
        barcodePrinting: documentRef.barcodePrinting,
        electronicFlg: documentRef.electronicFlg,
        paperFlg: documentRef.paperFlg,
      });
      if (documentRef.documents.length) {
        const editingVersion = documentRef.documents.find((doc) => doc.versionNo === editingVersionNo);
        setEditingVersionState(editingVersion);
        if (editingVersion) updateVersion(editingVersion);
      }
    }
  }, [documentRef]);

  useEffect(() => {
    fileAttachmentRefs.current = fileAttachmentRefs.current.slice(0, form.attachmentFileList.length);
  }, [form.attachmentFileList]);

  const describeDocumentType = (docTypeId: number) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < documentClassifications.length; i++) {
      // eslint-disable-next-line no-plusplus
      for (let j = 0; j < documentClassifications[i].types.length; j++) {
        const docType = documentClassifications[i].types[j];
        if (docType.documentTypeId === docTypeId) {
          return docType.documentTypeName;
        }
      }
    }
    return '';
  };

  const handlePreviewPdf = () => {
    if (documentVersion) {
      createDownloadLog(documentVersion.dataId, 1);
    }
    setPdfForModal(form.file ? form.file : null);
  };

  const handleAddAttachmentFile = () => {
    inputAttachmentFile.current?.click();
    if (inputAttachmentFile.current) {
      inputAttachmentFile.current.value = '';
    }
  };

  const onAttachmentFileSelected = useCallback(async (value: FileList | null) => {
    const attachmentFileError = Validator.validateAttachmentFile(value?.item(0) as File);

    if (attachmentFileError) {
      openMessageModal(attachmentFileError);
      return;
    }

    const existingAttachment = form.attachmentFileList.find(
      (attachment) => !attachment.deleted && attachment.fileName === value?.item(0)?.name,
    );

    if (existingAttachment) {
      await openMessageModal(ERROR_MESSAGES.DUPLICATE_FILE_NAME_ERROR);
      return;
    }

    const fileNameError = Validator.validateAttachmentFileName(value?.item(0)?.name);

    const newForm = {
      ...form,
      attachmentFileList: [...form.attachmentFileList, {
        file: value?.item(0) as File, fileName: value?.item(0)?.name, fileSize: value?.item(0)?.size, fileNameError,
      }],
    };
    const isTooLarge = await Validator.validateDocumentUpdateFormIsTooLarge(newForm);
    setIsFormBodySizeTooLarge(isTooLarge);
    setForm(newForm);
  }, [form, openMessageModal]);

  const onDeleteFile = async () => {
    const newForm = {
      ...form, file: undefined, fileName: '', fileSize: 0, fileId: undefined, fileNameError: '', fileError: '',
    };
    const isTooLarge = await Validator.validateDocumentUpdateFormIsTooLarge(newForm);
    setIsFormBodySizeTooLarge(isTooLarge);
    setForm(newForm);
  };

  const renderDocumentFileInput = () => {
    const documentFileInputWithFileSelected = (
      <>
        <div className={mainStyles['flex-1']}>
          <div className={`${styles.width280px} ${styles.text} ${mainStyles.expandDiv}`}>
            {form.file ? form.file.name : form.fileName}
          </div>
        </div>
        <div className={[mainStyles['d-flex'], mainStyles['flex-4'], mainStyles['ml-4']].join(' ')}>
          <Button className={[mainStyles['small-button']].join(' ')} color="lighterGray" size="smaller" onClick={() => fileSelectorRef.current?.click()}>
            <img src="/images/Icon-metro-attachment.svg" alt="" />
          </Button>
          {(form.file) && (
            <>
              <Button
                className={[mainStyles['small-button'], mainStyles['ml-10']].join(' ')}
                color="lightPrimary"
                size="smaller"
                onClick={onDownloadClick}
              >
                <img src="/images/Icon-metro-file-download.svg" alt="" />
              </Button>
              <Button
                className={[mainStyles['small-button'], mainStyles['ml-10']].join(' ')}
                color="lightPrimary"
                size="smaller"
                onClick={onDeleteFile}
              >
                <img src="/images/Icon-feather-trash-2.svg" alt="" />
              </Button>
            </>
          )}
        </div>
      </>
    );
    const documentFileInputWithoutFileSelected = (
      <Button
        color="lighterGray"
        size="smaller"
        style={{ width: 180 }}
        onClick={() => fileSelectorRef.current?.click()}
      >
        PDFファイルを追加
      </Button>
    );
    const documentFileInput = form.fileName ? documentFileInputWithFileSelected : documentFileInputWithoutFileSelected;
    return (
      <>
        <input
          type="file"
          ref={fileSelectorRef}
          style={{ display: 'none' }}
          accept="application/pdf"
          onChange={(e) => onFileSelected(e.target.files ? e.target.files[0] : undefined)}
        />
        {documentFileInput}
      </>
    );
  };

  return (
    <div className={styles.bodySection}>
      <div className={mainStyles['d-flex']}>
        <div
          className={[mainStyles['flex-1'], mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['justify-content-end']].join(' ')}
        >
          <SelectInput
            disabled
            value={documentVersion}
            options={documentRef?.documents?.sort((a, b) => b.versionNo - a.versionNo).map((item) => ({ text: `第${item.versionNo}版`, value: item })) || []}
            onChange={updateVersion}
            style={{ width: '170px', marginRight: 30 }}
          />
        </div>
      </div>
      <div className={styles.bodySectionMain}>

        <div className={styles.pdfContainer}>
          <PdfPreview className={styles.imagesContainerSection} pdfFile={form.file || undefined} pageHeight={300} scrollMode={PdfScrollMode.HORIZONTAL} />
          {form.file && (
            <img
              className={styles.arrowImg}
              src="/images/provisory-images/arrow-up-right.svg"
              alt=""
              onClick={handlePreviewPdf}
            />
          )}
        </div>
        <div className={styles.formContainer}>
          <div className={[mainStyles['d-flex'], mainStyles['mt-20px']].join(' ')}>
            <div className={styles.label}>
              文書名
            </div>
            <div>
              <InputAutoSize
                className={styles.formInput}
                value={form.name}
                onChange={(name) => setForm({ ...form, name })}
                invalidErrorMessage={ERROR_MESSAGES.LENGTH_ERROR}
                isInvalidValue={Validator.isInvalidDocumentName(form.name)}
              />
            </div>
          </div>
          <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              PDFファイル名
            </div>
            {renderDocumentFileInput()}
          </div>
          {form.fileNameError && (
            <div className={mainStyles['text-danger']} style={{ marginLeft: '120px' }}>
              {form.fileNameError}
            </div>
          )}
          {(isFormBodySizeTooLarge && form.file) && (
            <div className={mainStyles['text-danger']} style={{ marginLeft: '120px' }}>
              {ERROR_MESSAGES.FILE_SIZE_LIMIT_ERROR_MESSAGE}
            </div>
          )}
          {form.fileError && (
            <div className={mainStyles['text-danger']} style={{ marginLeft: '120px' }}>
              {form.fileError}
            </div>
          )}
          <div className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              文書種類
            </div>
            <div>
              <SelectInput
                className={styles.formSelect}
                value={documentTypeOptions.find((item) => item.value.type.documentTypeId === form.typeId && item.value.documentClassification.id === form.classification)?.value}
                options={documentTypeOptions}
                onChange={handleChangeDocumentType}
              />
            </div>
          </div>
          {selectedClassification?.text.startsWith('契約書') && (
          <>
            <div
              className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['mt-15px']].join(' ')}
            >
              <div className={styles.label}>契約種別</div>
              <div
                className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['w-100'], mainStyles['ml-4'], styles.text].join(' ')}
              >
                <RadioGroupInput value={documentPrintedTypeSelected} onChange={onDocumentPrintedTypeSelected}>
                  <RadioGroupInput.RadioInput value="ELECTRONIC">電子契約</RadioGroupInput.RadioInput>
                  <RadioGroupInput.RadioInput value="PAPER">書面契約</RadioGroupInput.RadioInput>
                </RadioGroupInput>
              </div>
            </div>
            <div
              className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['mt-15px']].join(' ')}
            >
              <div className={styles.label}>書面有無</div>
              <div
                className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['w-100'], mainStyles['ml-4'], styles.text].join(' ')}
              >
                { documentPrintedTypeSelected === 'ELECTRONIC' ? (
                  <RadioGroupInput value="" onChange={() => setForm({ ...form, paperFlg: 0 })}>
                    <RadioGroupInput.RadioInput disabled value="1">有り</RadioGroupInput.RadioInput>
                    <RadioGroupInput.RadioInput disabled value="" style={{ marginLeft: '70px' }}>無し</RadioGroupInput.RadioInput>
                  </RadioGroupInput>
                ) : (
                  <RadioGroupInput
                    value={form.paperFlg === 1 ? '1' : '0'}
                    onChange={(value) => setForm(
                      {
                        ...form,
                        paperFlg: value === '1' ? 1 : 0,
                      },
                    )}
                  >
                    <RadioGroupInput.RadioInput value="1">有り</RadioGroupInput.RadioInput>
                    <RadioGroupInput.RadioInput value="0" style={{ marginLeft: '70px' }}>無し</RadioGroupInput.RadioInput>
                  </RadioGroupInput>
                )}
              </div>
            </div>
            <div className={`${mainStyles['d-flex']} ${mainStyles['mt-15px']}`}>
              <div className={styles.label}>
                <div>
                  バーコード
                </div>
                {' '}
                印字欄
              </div>
              <div>
                <InputAutoSize
                  disabled={(documentRef?.barcodeChangeable !== 1 || form.paperFlg === 0)}
                  className={`${mainStyles.input} ${styles.width280px}`}
                  value={form.barcodePrinting}
                  onChange={(barcodePrinting) => setForm({ ...form, barcodePrinting })}
                />
                <div className={mainStyles['text-danger']}>
                  ※入力した値が入庫時に貼り付けるバーコードに印字されます。
                  入庫後の値の変更はできません
                </div>
              </div>
            </div>
          </>
          )}
          <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              タグ
            </div>
            <div className={[styles.tagTextContent, styles.text].join(' ')}>
              {form.tagList?.map((tag) => (
                <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')} key={tag.tagLabel}>
                  <div
                    className={[styles.text, mainStyles.expandDiv].join(' ')}
                    style={{ width: '170px', minWidth: '170px', marginRight: '10px' }}
                  >
                    {tag.tagLabel}
                  </div>
                  <div style={{ width: '20px' }}>
                    { tag.required && (
                    <div className={` ${mainStyles['text-danger']} ${mainStyles['d-flex']} ${mainStyles['justify-content-center']}`} style={{ marginRight: '10px' }}>
                      ※
                    </div>
                    )}
                  </div>
                  <TagInput
                    tag={tag}
                    onChange={(value, isValidValue) => setDocumentVersionTagValue(tag, 'value', value, isValidValue)}
                    className={styles.width280px}
                    isDuplicated={(tag as any).isDuplicated || false}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              メモ
            </div>
            <div style={{ width: 'calc(100% - 110px)' }}>
              <textarea
                className={styles.textArea}
                value={form.memo || ''}
                onChange={(e) => setForm({ ...form, memo: e.target.value })}
              />
            </div>
          </div>
          <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              関連文書
            </div>
            <div style={{ width: '100%' }}>
              { form?.relationalDocumentList?.map((relationalDocument) => (
                <div className={styles.relationalDocumentDetail} key={uuid()}>
                  <div style={{ width: '200px' }} className={[styles.text, mainStyles.expandDiv].join(' ')}>
                    {`文書ID：${relationalDocument.ccControlNumber}`}
                  </div>
                  <div style={{ width: '200px' }} className={mainStyles.expandDiv}>
                    {`文書名：${relationalDocument.name}`}
                  </div>
                  <div style={{ width: '200px', marginLeft: '15px' }} className={mainStyles.expandDiv}>
                    {`文書種類：${describeDocumentType(+(relationalDocument.type ?? '0'))}`}
                  </div>
                  <button
                    type="button"
                    className={`${mainStyles.button} ${mainStyles['btn-primary-light']} ${mainStyles['small-button']} ${mainStyles['d-flex']} ${mainStyles['align-items-center']} ${mainStyles['justify-content-center']}`}
                    onClick={() => { removeRelatedDocument(relationalDocument); }}
                  >
                    <img src="/images/Icon-feather-trash-2.svg" alt="" />
                  </button>
                </div>
              ))}
              <Button
                color="lighterGray"
                size="smaller"
                onClick={() => {
                  setChoosingRelatedDocument(true);
                }}
              >
                文書参照
              </Button>
            </div>
          </div>
          <div className={[mainStyles['d-flex'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              添付ファイル
            </div>
            <div style={{ width: '100%' }}>
              {form.attachmentFileList.map((attachment, index) => (
                <div className={mainStyles['mb-15px']} key={uuid()}>
                  <div className={mainStyles['d-flex']}>
                    <div className={mainStyles['flex-1']}>
                      <TextareaAutosize
                        disabled
                        style={{ minHeight: '25px', paddingTop: '4px' }}
                        className={[
                          mainStyles.textareaAutosize,
                          styles.formInput,
                          styles.disabledColor,
                          attachment.deleted ? styles.deletedTextStyle : '',
                          ((isFormBodySizeTooLarge && !attachment.id) || attachment.fileNameError) ? mainStyles.invalidBorder : '',
                        ].join(' ')}
                        value={attachment.fileName}
                        placeholder="xxxxx.txt"
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDocumentAttachmentValue(attachment, 'fileName', e.target.value)}
                      />
                    </div>
                    <div className={[mainStyles['d-flex'], mainStyles['flex-4'], mainStyles['ml-4']].join(' ')}>
                      <Button
                        className={[mainStyles['small-button']].join(' ')}
                        color="lightPrimary"
                        size="smaller"
                        onClick={() => attachment && downloadAttachmentFile(attachment, attachment.fileName)}
                      >
                        <img src="/images/Icon-metro-file-download.svg" alt="" />
                      </Button>
                      {!attachment.deleted && (
                        <Button className={[mainStyles['small-button'], mainStyles['ml-10']].join(' ')} color="lightPrimary" size="smaller" onClick={() => removeAttachmentFile(attachment)}>
                          <img src="/images/Icon-feather-trash-2.svg" alt="" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {attachment.fileNameError && (
                    <div className={mainStyles['text-danger']}>
                      {attachment.fileNameError}
                    </div>
                  )}
                  {(isFormBodySizeTooLarge && !attachment.id && index === form.attachmentFileList.length - 1) && (
                    <div className={mainStyles['text-danger']}>
                      {ERROR_MESSAGES.FILE_SIZE_LIMIT_ERROR_MESSAGE}
                    </div>
                  )}
                </div>
              ))}
              <Button
                color="lighterGray"
                size="smaller"
                style={{ width: 180 }}
                onClick={handleAddAttachmentFile}
              >
                ファイルを追加
              </Button>
            </div>
          </div>

          <div className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              フォルダ
            </div>
            <div className={styles.text}>
              {documentRef?.folder.name}
            </div>
          </div>
          <div className={[mainStyles['d-flex'], mainStyles['align-items-center'], mainStyles['mt-15px']].join(' ')}>
            <div className={styles.label}>
              ステータス
            </div>
            <div className={[mainStyles['flex-1'], styles.text].join(' ')}>
              { documentRef && documentRef.warehouseStatus }
            </div>
          </div>
        </div>
        <div className={styles.footerButton}>
          {documentRef && hasFolderPermission(documentRef.folder, ControlCode.Delete) && (
            <div>
              <Button
                color="error"
                onClick={onDeleteRegisterClick}
                size="smaller"
                loading={documentDelete.loading}
                style={{ marginRight: 10 }}
                disabled={!!documentRef.itemCode}
                disabledColor="var(--lighterGray)"
              >
                文書を削除
              </Button>
              {
              (documentRef?.documents.length || 0) > 1 && (
                <Button color="error" onClick={onDeleteRevisionClick} size="smaller" className={styles.deleteRevisionButtonWidth} loading={documentDeleteRevision.loading} disabled={isDeletionDisabled}>
                  表示中の版情報を削除
                </Button>
              )
              }
            </div>
          )}
          <div />
          {documentRef && hasFolderPermission(documentRef.folder, ControlCode.Write) && (
            <Button
              isRequestingButton
              loading={documentUpdate.loading || documentRevision.loading || isCheckingDuplicates}
              color="var(--primary)"
              size="smaller"
              onClick={onRegister}
              disabled={isButtonRegisterDisabled}
              disabledColor="var(--lighterGray)"
            >
              登録
            </Button>
          )}
        </div>
        <BaseModal open={operationModal} style={{ width: '530px' }}>
          <div className={[styles.alertMessage].join(' ')}>
            編集した文書を改版しますか？
            <br />
            改版せずに内容を差し替えますか？
          </div>
          <div className={style.alertButtons} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              loading={documentUpdate.loading}
              size="smaller"
              onClick={() => {
                onSaveDocument('update');
                setOperationModal(false);
              }}
            >
              差し替える
            </Button>
            <Button
              loading={documentRevision.loading}
              size="smaller"
              onClick={() => {
                onSaveDocument('revision');
                setOperationModal(false);
              }}
            >
              改版する
            </Button>
            <Button
              color="lightGray"
              size="smaller"
              onClick={() => setOperationModal(false)}
            >
              キャンセル
            </Button>
          </div>
        </BaseModal>
        { (form.file && pdfForModal) && (
        <PdfPreviewModal
          open
          onClose={() => setPdfForModal(null)}
          pdfFile={pdfForModal}
          fileName={documentVersion?.fileName || ''}
          fileId={documentVersion?.dataId || 0}
          showOcrSearchPreview={documentVersion?.documentFileOcrFlg === true}
          documentVersionNo={documentVersion?.versionNo}
          documentId={documentRef?.id}
          highlightTexts={highlightTexts}
          searchKeyword={searchKeyword}
        />
        )}
        {choosingRelatedDocument && (
        <ChooseRelatedDocument
          onChooseRelatedDocuments={(docs) => {
            handleRelatedDocuments(docs);
          }}
          onCancelAddingRelatedDocuments={() => {
            onCancelAddingRelatedDocuments();
            setChoosingRelatedDocument(false);
          }}
        />
        )}
      </div>
      <input
        type="file"
        id="file"
        ref={inputAttachmentFile}
        style={{ display: 'none' }}
        onChange={(e) => onAttachmentFileSelected(e.target.files)}
      />
      <AlertModal
        modalStyle={modal.modalStyle}
        open={modal.open}
        text={modal.text}
        confirmText={modal.confirmText}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
        noCancel={modal.noCancel}
      />
    </div>

  );
}

export default DocDetailEdit;
